import { useState, type ReactNode } from 'react';
import { tapOf, type Tile, type TileStyle } from '@casa/model';
import { useTapCommand } from '../view/tap_command.ts';
import { TileFrame } from '../view/tile_frame.tsx';
import { useTileEnvironment } from '../view/tile_environment.ts';
import { ResolvedTileStyleContext, TileStyleOverrideContext, TileTextColorContext, tileStyleOf } from '../view/tile_style.ts';
import { TileHighlightContext } from '../view/tile_highlight.ts';
import { ActionTileView } from './action/action_tile_view.tsx';
import { AirQualityTileView } from './air_quality/air_quality_tile_view.tsx';
import { ForecastTileView } from './forecast/forecast_tile_view.tsx';
import { GraphTileView } from './graph/graph_tile_view.tsx';
import { ImageTileView } from './image/image_tile_view.tsx';
import { LightTileView } from './light/light_tile_view.tsx';
import { SolarTileView } from './solar/solar_tile_view.tsx';
import { StatusTileView } from './status/status_tile_view.tsx';
import { SwitchTileView } from './switch/switch_tile_view.tsx';
import { TableTileView } from './table/table_tile_view.tsx';
import { TextTileView } from './text/text_tile_view.tsx';
import { ThermostatTileView } from './thermostat/thermostat_tile_view.tsx';
import { ValueTileView } from './value/value_tile_view.tsx';
import { VideoTileView } from './video/video_tile_view.tsx';

// Any tile: the view of its type, in the frame every tile has, and its tap where its type has none
// of its own (tiles spec §6). The app shows a tile with this and provides its data with
// `TileEnvironmentContext`.

function TileContent({ tile }: { tile: Tile }) : ReactNode {
    switch (tile.type) {
        case 'value':
            return <ValueTileView tile={tile} />;
        case 'graph':
            return <GraphTileView tile={tile} />;
        case 'solar':
            return <SolarTileView tile={tile} />;
        case 'thermostat':
            return <ThermostatTileView tile={tile} />;
        case 'airquality':
            return <AirQualityTileView tile={tile} />;
        case 'video':
            return <VideoTileView tile={tile} />;
        case 'image':
            return <ImageTileView tile={tile} />;
        case 'light':
            return <LightTileView tile={tile} />;
        case 'switch':
            return <SwitchTileView tile={tile} />;
        case 'action':
            return <ActionTileView tile={tile} />;
        case 'table':
            return <TableTileView tile={tile} />;
        case 'status':
            return <StatusTileView tile={tile} />;
        case 'forecast':
            return <ForecastTileView tile={tile} />;
        case 'text':
            return <TextTileView tile={tile} />;
    }
}

/** What a tap on the tile does (TA-2), with the tap feedback of a command; undefined where it has no tap. */
function useTileTap(tile: Tile) : (() => void) | undefined {
    const environment = useTileEnvironment();
    const tap = tapOf(tile);
    const send = useTapCommand(tile, tap?.kind === 'command' ? tap.source : undefined);

    switch (tap?.kind) {
        case undefined:
            return undefined;
        case 'screen':
            return () => environment.showScreen?.(tap.screen);
        case 'command':
            return () => void send(tap.command, tap.argument);
    }
}

/** A tile in its frame, dimmed and marked as its sources' status says. */
export function TileView({ tile }: { tile: Tile }) : ReactNode {
    const [highlight, setHighlight] = useState<string | undefined>();

    return (
        <TileHighlightContext.Provider value={setHighlight}>
            <FramedTile tile={tile} highlight={highlight} />
        </TileHighlightContext.Provider>
    );
}

/** The tile in its frame, inside the highlight's context, which its tap and its view set. */
function FramedTile({ tile, highlight }: { tile: Tile; highlight: string | undefined }) : ReactNode {
    const environment = useTileEnvironment();
    const { status, failures } = environment.useStatus(tile);
    // A rule's style over the tile's own (a value tile's style rules, dashboard-editor spec §5.10).
    const [override, setOverride] = useState<TileStyle | undefined>();
    const style = tileStyleOf(environment.tileStyle, override ? { ...tile.style, ...override } : tile.style);
    const onTap = useTileTap(tile);

    return (
        <TileFrame
            label={tile.label}
            titleIcon={tile.titleIcon}
            dimmed={status === 'stale' || status === 'failing'}
            failures={status === 'failing' ? failures : []}
            background={highlight}
            tileStyle={style}
            onTap={onTap}
        >
            <TileStyleOverrideContext.Provider value={setOverride}>
                <ResolvedTileStyleContext.Provider value={style}>
                    <TileTextColorContext.Provider value={style.text}>
                        <TileContent tile={tile} />
                    </TileTextColorContext.Provider>
                </ResolvedTileStyleContext.Provider>
            </TileStyleOverrideContext.Provider>
        </TileFrame>
    );
}
